/**
 * ============================================================================
 * SECURITY SERVICE - Frontend
 * ============================================================================
 * 
 * Frontend service for security monitoring and management:
 * - Security events and logs
 * - Login history
 * - Active sessions
 * - Security alerts
 * - Account security settings
 */

import api from './api';

/**
 * Handle API errors
 */
const handleApiError = (error) => {
  if (error.response) {
    return error.response.data;
  }
  return { message: error.message || 'An error occurred' };
};

/**
 * Get security events for user
 * @param {Object} filters - Filter options
 * @param {string} filters.type - Event type filter
 * @param {string} filters.startDate - Start date (ISO format)
 * @param {string} filters.endDate - End date (ISO format)
 * @param {number} filters.page - Page number
 * @param {number} filters.limit - Items per page
 * @returns {Promise<Object>} Security events with pagination
 */
export const getSecurityEvents = async (filters = {}) => {
  try {
    const response = await api.get('/security/events', {
      params: filters,
    });
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get login history
 * @param {Object} filters - Filter options
 * @param {string} filters.startDate - Start date (ISO format)
 * @param {string} filters.endDate - End date (ISO format)
 * @param {number} filters.page - Page number
 * @param {number} filters.limit - Items per page
 * @returns {Promise<Object>} Login history with pagination
 */
export const getLoginHistory = async (filters = {}) => {
  try {
    const response = await api.get('/security/login-history', {
      params: filters,
    });
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get active sessions
 * @returns {Promise<Array>} Array of active sessions
 */
export const getActiveSessions = async () => {
  try {
    const response = await api.get('/security/sessions');
    return response.data.sessions;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Revoke session
 * @param {string} sessionId - Session ID to revoke
 * @returns {Promise<void>}
 */
export const revokeSession = async (sessionId) => {
  try {
    await api.delete(`/security/sessions/${sessionId}`);
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Revoke all other sessions (keep current)
 * @returns {Promise<void>}
 */
export const revokeAllOtherSessions = async () => {
  try {
    await api.post('/security/sessions/revoke-all');
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get security alerts
 * @param {Object} filters - Filter options
 * @param {string} filters.severity - Severity filter (low, medium, high, critical)
 * @param {boolean} filters.read - Read/unread filter
 * @param {number} filters.page - Page number
 * @param {number} filters.limit - Items per page
 * @returns {Promise<Object>} Security alerts with pagination
 */
export const getSecurityAlerts = async (filters = {}) => {
  try {
    const response = await api.get('/security/alerts', {
      params: filters,
    });
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Mark security alert as read
 * @param {string} alertId - Alert ID
 * @returns {Promise<void>}
 */
export const markAlertAsRead = async (alertId) => {
  try {
    await api.patch(`/security/alerts/${alertId}/read`);
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get security settings
 * @returns {Promise<Object>} Security settings
 */
export const getSecuritySettings = async () => {
  try {
    const response = await api.get('/security/settings');
    return response.data.settings;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Update security settings
 * @param {Object} settings - Security settings to update
 * @returns {Promise<Object>} Updated security settings
 */
export const updateSecuritySettings = async (settings) => {
  try {
    const response = await api.patch('/security/settings', settings);
    return response.data.settings;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get suspicious activity report
 * @param {Object} filters - Filter options
 * @param {string} filters.startDate - Start date (ISO format)
 * @param {string} filters.endDate - End date (ISO format)
 * @returns {Promise<Object>} Suspicious activity report
 */
export const getSuspiciousActivity = async (filters = {}) => {
  try {
    const response = await api.get('/security/suspicious-activity', {
      params: filters,
    });
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Report suspicious activity
 * @param {Object} report - Suspicious activity report
 * @param {string} report.type - Activity type
 * @param {string} report.description - Description
 * @param {Object} report.metadata - Additional metadata
 * @returns {Promise<void>}
 */
export const reportSuspiciousActivity = async (report) => {
  try {
    await api.post('/security/report-suspicious', report);
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get security statistics
 * @param {Object} filters - Filter options
 * @param {string} filters.startDate - Start date (ISO format)
 * @param {string} filters.endDate - End date (ISO format)
 * @returns {Promise<Object>} Security statistics
 */
export const getSecurityStatistics = async (filters = {}) => {
  try {
    const response = await api.get('/security/statistics', {
      params: filters,
    });
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Enable login alerts
 * @param {Object} options - Alert options
 * @param {boolean} options.email - Enable email alerts
 * @param {boolean} options.sms - Enable SMS alerts
 * @param {boolean} options.push - Enable push alerts
 * @returns {Promise<void>}
 */
export const enableLoginAlerts = async (options) => {
  try {
    await api.post('/security/login-alerts/enable', options);
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Disable login alerts
 * @returns {Promise<void>}
 */
export const disableLoginAlerts = async () => {
  try {
    await api.post('/security/login-alerts/disable');
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get IP whitelist
 * @returns {Promise<Array>} Array of whitelisted IPs
 */
export const getIPWhitelist = async () => {
  try {
    const response = await api.get('/security/ip-whitelist');
    return response.data.ips;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Add IP to whitelist
 * @param {string} ip - IP address or CIDR range
 * @returns {Promise<void>}
 */
export const addIPToWhitelist = async (ip) => {
  try {
    await api.post('/security/ip-whitelist', { ip });
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Remove IP from whitelist
 * @param {string} ip - IP address
 * @returns {Promise<void>}
 */
export const removeIPFromWhitelist = async (ip) => {
  try {
    await api.delete('/security/ip-whitelist', {
      params: { ip },
    });
  } catch (error) {
    throw handleApiError(error);
  }
};

export default {
  getSecurityEvents,
  getLoginHistory,
  getActiveSessions,
  revokeSession,
  revokeAllOtherSessions,
  getSecurityAlerts,
  markAlertAsRead,
  getSecuritySettings,
  updateSecuritySettings,
  getSuspiciousActivity,
  reportSuspiciousActivity,
  getSecurityStatistics,
  enableLoginAlerts,
  disableLoginAlerts,
  getIPWhitelist,
  addIPToWhitelist,
  removeIPFromWhitelist,
};
